'use client'

import type { AbstractIntlMessages } from 'next-intl'
import { NextIntlClientProvider } from 'next-intl'
import { type ReactNode, useEffect, useLayoutEffect, useState, useSyncExternalStore } from 'react'
import { DEFAULT_LOCALE, type SupportedLocale } from '@/i18n/locales'
import {
  applyClientLocale,
  getClientLocale,
  resolveClientLocale,
  subscribeToClientLocale,
} from '@/lib/i18n/client-locale'
import enOverlayMessages from '@/lib/i18n/root-overlay-messages/en'

const overlayMessageLoaders = {
  en: () => Promise.resolve({ default: enOverlayMessages }),
  zh: () => import('@/lib/i18n/root-overlay-messages/zh'),
  hi: () => import('@/lib/i18n/root-overlay-messages/hi'),
  es: () => import('@/lib/i18n/root-overlay-messages/es'),
  fr: () => import('@/lib/i18n/root-overlay-messages/fr'),
  ar: () => import('@/lib/i18n/root-overlay-messages/ar'),
  bn: () => import('@/lib/i18n/root-overlay-messages/bn'),
  pt: () => import('@/lib/i18n/root-overlay-messages/pt'),
  ru: () => import('@/lib/i18n/root-overlay-messages/ru'),
  ja: () => import('@/lib/i18n/root-overlay-messages/ja'),
  it: () => import('@/lib/i18n/root-overlay-messages/it'),
  he: () => import('@/lib/i18n/root-overlay-messages/he'),
  de: () => import('@/lib/i18n/root-overlay-messages/de'),
  el: () => import('@/lib/i18n/root-overlay-messages/el'),
  pl: () => import('@/lib/i18n/root-overlay-messages/pl'),
} satisfies Record<SupportedLocale, () => Promise<{ default: AbstractIntlMessages }>>

const loadedOverlayMessages = new Map<SupportedLocale, AbstractIntlMessages>([
  [DEFAULT_LOCALE, enOverlayMessages],
])

// Carries only the overlay message subset for UI mounted directly in the root
// layout (toasts, impersonation banner, chunk recovery, offline/404). Section
// subtrees layer their full server-resolved messages on top of this provider
// via ScopedIntlProvider.
export function RootIntlProvider({ children }: { children: ReactNode }) {
  const locale = useSyncExternalStore(
    subscribeToClientLocale,
    getClientLocale,
    () => DEFAULT_LOCALE
  )
  const [, setLoadedCount] = useState(0)
  const messages = loadedOverlayMessages.get(locale) ?? enOverlayMessages

  // Dev Strict Mode remounts reset <html> to its JSX attributes, discarding
  // what the inline locale script set during parsing; re-apply from the same
  // source. In production this re-applies identical values and settles.
  useLayoutEffect(() => {
    applyClientLocale(resolveClientLocale())
  }, [])

  useEffect(() => {
    if (loadedOverlayMessages.has(locale)) {
      return
    }
    let cancelled = false
    overlayMessageLoaders[locale]()
      .then((module_: { default: AbstractIntlMessages }) => {
        loadedOverlayMessages.set(locale, module_.default)
        if (!cancelled) {
          setLoadedCount((count) => count + 1)
        }
      })
      .catch((error: unknown) => {
        console.error(`Failed to load root overlay messages for locale ${locale}:`, error)
      })
    return () => {
      cancelled = true
    }
  }, [locale])

  return (
    // timeZone pins what the request-scoped provider previously inherited from
    // the server runtime (UTC in production); without it next-intl raises
    // ENVIRONMENT_FALLBACK on every formatter call under this provider.
    <NextIntlClientProvider locale={locale} messages={messages} timeZone="UTC">
      {children}
    </NextIntlClientProvider>
  )
}
